import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import useReusableOCR from './hooks/useReusableOCR';
import ReusableFileUploadArea from './components/ReusableFileUploadArea';
import ReusableManualEntryForm from './components/ReusableManualEntryForm';
import ReusableOCRFailureModal from './components/ReusableOCRFailureModal';

const ReusableOCRFlow = ({
    // OCR Configuration
    ocrService,
    title = "Add Details",
    
    // OCR Service Options
    consent = 'Y',
    allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
    maxSize = 5 * 1024 * 1024,
    extractorFunction,
    
    // Upload Configuration
    uploadTitle = "Upload Document",
    uploadedTitle = "Uploaded Document",
    processingText = "Processing Document...",
    acceptedFileTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
    
    // Form Configuration
    requirePhone = true,
    requireEmail = false,
    requireRelation = false,
    relations = [],
    minAge = 18,
    maxAge = 60,
    context = 'beneficiary',
    
    // Document Type Selection
    showDocumentTypeSelection = false,
    selectedDocumentType = 'AADHAAR',
    onDocumentTypeChange,
    relationData = null, // Pass the relation data to determine chip visibility
    
    // UI Configuration
    showConfirmModal = true,
    confirmModalTitle = "Confirm Details",
    saveButtonText = "Save & Continue",
    editManuallyText = "No, Fill Manually",
    confirmText = "Yes",
    confirmationCheckboxText = "Details provided herein are true and I have obtained all relevant organisational consents for the purposes.",
    
    // Manual Form Configuration
    showCancelButton = true,
    cancelButtonText = "Upload Image",
    
    // Failure Modal Configuration
    failureModalTitle = "Document Invalid",
    tryAgainText = "Try Again",
    enterManuallyText = "Enter Manually",
    
    // Additional Customization
    additionalFields = [],
    customValidation,
    
    // Callbacks
    onSave,
    onCancel,
    onSuccess,
    onError,
    onFieldChange,
    onStepChange,
    
    // Initial Data
    initialData = {},
    
    // Advanced Options
    enableToast = true,
    autoOpenFailureModal = true,
    skipConfirmModal = false,
    
    // Reset Control
    resetTrigger = 0,
    
    // Age warning modal props
    owner_type_text,
    onRelationUpdate
}) => {
    const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'manual'
    const [phoneNumber, setPhoneNumber] = useState(initialData.phone_number || '');
    const [relation, setRelation] = useState(
        initialData.relation || 
        (relations && relations.length > 0 && requireRelation ? relations[0].value : '')
    );
    const [ocrFailureModalOpen, setOcrFailureModalOpen] = useState(false);
    const [currentRelations, setCurrentRelations] = useState(relations);

    useEffect(() => {
        setCurrentRelations(relations);
    }, [relations]);
    const handleRelationUpdate = (newRelations) => {
        setCurrentRelations(newRelations);
        if (newRelations && newRelations.length > 0) {
            const newRelation = newRelations[0].value || newRelations[0];
            setRelation(newRelation);
            if (onFieldChange) {
                onFieldChange('relation', newRelation);
            }
        }
        if (onRelationUpdate) {
            onRelationUpdate(newRelations);
        }
    };

    // Set default relation when relations prop changes and no current relation is set
    useEffect(() => {
        if (requireRelation && currentRelations && currentRelations.length > 0 && !relation) {
            const defaultRelation = currentRelations[0].value || currentRelations[0];
            setRelation(defaultRelation);
            if (onFieldChange) {
                onFieldChange('relation', defaultRelation);
            }
        }
    }, [currentRelations, requireRelation, relation, onFieldChange]);

    // Initialize OCR hook
    const {
        isLoading,
        error,
        extractedData,
        uploadedFile,
        handleFileUpload,
        resetOCR
    } = useReusableOCR({
        ocrService,
        consent,
        allowedTypes,
        maxSize,
        extractorFunction,
        enableToast,
        onSuccess: (data, response) => {
            if (onSuccess) {
                onSuccess(data, response);
            }
        },
        onError: (errorMessage, response) => {
            if (autoOpenFailureModal) {
                setOcrFailureModalOpen(true);
            }
            if (onError) {
                onError(errorMessage, response);
            }
        }
    });

    // Reset OCR flow when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            setCurrentStep('upload');
            setOcrFailureModalOpen(false);
            resetOCR();
            setPhoneNumber(initialData.phone_number || '');
            setRelation(
                initialData.relation || 
                (currentRelations && currentRelations.length > 0 && requireRelation ? currentRelations[0].value : '')
            );
            // Don't reset currentRelations to preserve blood relations if they were updated
            if (!currentRelations || currentRelations.length === 0) {
                setCurrentRelations(relations);
            }
        }
    }, [resetTrigger, requireRelation, resetOCR, relations, currentRelations]);

    // Handle file upload with OCR processing
    const handleFileUploadWithOCRHandling = async (file) => {
        const success = await handleFileUpload(file);
        if (!success && autoOpenFailureModal) {
            setOcrFailureModalOpen(true);
        }
        return success;
    };

    // Handle step changes
    const changeStep = (newStep) => {
        setCurrentStep(newStep);
        if (onStepChange) {
            onStepChange(newStep);
        }
    };

    // OCR Failure Modal Handlers
    const handleTryAgain = () => {
        setOcrFailureModalOpen(false);
        changeStep('upload');
        resetOCR();
    };

    const handleEnterManually = () => {
        setOcrFailureModalOpen(false);
        changeStep('manual');
    };

    // Navigation handlers
    const handleBackToUpload = () => {
        changeStep('upload');
        resetOCR();
        setPhoneNumber(initialData.phone_number || '');
        setRelation(initialData.relation || '');
    };

    const handleEditManually = () => {
        changeStep('manual');
    };
    const handleConfirmExtractedData = async (formData = {}) => {
        const combinedData = {
            ...extractedData,
            phone_number: phoneNumber,
            relation: relation,
            ocr_verified: currentStep === 'upload',
            ...formData
        };

        if (onSave) {
            await onSave(combinedData);
        }
    };

    const handleManualSave = async (formData) => {
        const combinedData = {
            ...formData,
            phone_number: formData.phone_number || phoneNumber,
            relation: formData.relation || relation,
            ocr_verified: false
        };
        if (onSave) {
            await onSave(combinedData);
        }
    };

    const handleFieldChange = (fieldName, value) => {
        if (fieldName === 'phone_number') {
            setPhoneNumber(value);
        } else if (fieldName === 'relation') {
            setRelation(value);
        }

        if (onFieldChange) {
            onFieldChange(fieldName, value);
        }
    };
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: currentStep === 'manual' ? 1 : 0 }}>
            {title && (
                <Typography variant="h2">
                    {title}
                </Typography>
            )}

            {/* Upload Step */}
            {currentStep === 'upload' && (
                <ReusableFileUploadArea
                    onFileUpload={handleFileUploadWithOCRHandling}
                    onFileDelete={resetOCR}
                    isLoading={isLoading}
                    error={error}
                    uploadedFile={uploadedFile}
                    extractedData={extractedData}
                    phoneNumber={phoneNumber}
                    onPhoneChange={setPhoneNumber}
                    relation={relation}
                    onRelationChange={setRelation}
                    relations={currentRelations}
                    uploadTitle={uploadTitle}
                    uploadedTitle={uploadedTitle}
                    processingText={processingText}
                    acceptedFileTypes={acceptedFileTypes}
                    requirePhone={requirePhone}
                    requireRelation={requireRelation}
                    minAge={minAge}
                    maxAge={maxAge}
                    context={context}
                    onConfirm={handleConfirmExtractedData}
                    onEditManually={handleEditManually}
                    showConfirmModal={showConfirmModal && !skipConfirmModal}
                    confirmModalTitle={confirmModalTitle}
                    saveButtonText={saveButtonText}
                    editManuallyText={editManuallyText}
                    confirmText={confirmText}
                    confirmationCheckboxText={confirmationCheckboxText}
                    additionalFields={additionalFields}
                    customValidation={customValidation}
                    onFieldChange={handleFieldChange}
                    showDocumentTypeSelection={showDocumentTypeSelection}
                    selectedDocumentType={selectedDocumentType}
                    onDocumentTypeChange={onDocumentTypeChange}
                    relationData={relationData}
                    owner_type_text={owner_type_text}
                    onRelationUpdate={handleRelationUpdate}
                />
            )}

            {/* Manual Entry Step */}
            {currentStep === 'manual' && (
                <ReusableManualEntryForm
                    onSave={handleManualSave}
                    onCancel={showCancelButton ? handleBackToUpload : handleCancel}
                    initialData={{
                        full_name: extractedData.full_name || initialData.full_name,
                        dob: extractedData.dob || initialData.dob,
                        age: extractedData.age || initialData.age,
                        gender: extractedData.gender || initialData.gender,
                        phone_number: phoneNumber,
                        relation: relation,
                        email: initialData.email,
                    }}
                    requirePhone={requirePhone}
                    requireEmail={requireEmail}
                    requireRelation={requireRelation}
                    relations={currentRelations}
                    minAge={minAge}
                    maxAge={maxAge}
                    context={context}
                    showConfirmation={true}
                    confirmationText={confirmationCheckboxText}
                    saveButtonText={saveButtonText}
                    cancelButtonText={cancelButtonText}
                    showCancelButton={showCancelButton}
                    additionalFields={additionalFields}
                    customValidation={customValidation}
                    onFieldChange={onFieldChange}
                />
            )}

            {/* OCR Failure Modal */}
            <ReusableOCRFailureModal
                open={ocrFailureModalOpen}
                onClose={() => {
                    setOcrFailureModalOpen(false);
                    resetOCR();
                }}
                onTryAgain={handleTryAgain}
                onEnterManually={handleEnterManually}
                errorMessage={error}
                title={failureModalTitle}
                tryAgainText={tryAgainText}
                enterManuallyText={enterManuallyText}
            />
        </Box>
    );
};

export default ReusableOCRFlow;